import { Fragment, useState } from 'react'
import { ChevronRight } from 'lucide-react'

import type { FilterSearchParams } from '@/components/common/filters/types'
import type {
    InventoryDetailItem,
    InventoryStockFilter,
} from '@/lib/api/report-api'
import type { StatListItem } from '@/components/reports/stat-list-card'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { InventoryPositionRows } from '@/components/reports/inventory-position-rows'
import { StatListCard } from '@/components/reports/stat-list-card'
import { WarehouseMovementSection } from '@/components/reports/warehouse-movement-section'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
    useInventoryDetails,
    useInventoryPositions,
    useInventorySummary,
} from '@/hooks/use-report-query'
import { reportApi } from '@/lib/api/report-api'
import { formatQty, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** 'all' là giá trị mặc định của ô lọc, không phải một trạng thái tồn. */
type StockStatusFilter = 'all' | InventoryStockFilter

/**
 * Bộ lọc của bảng tồn kho, khai một lần theo chuẩn `defineListFilters`: parser
 * URL, mặc định, badge đếm, chip và debounce ô tìm kiếm đều do tầng chung lo.
 *
 * Export ra vì `validateSearch` thuộc về route `/reports/inventory` (và route
 * `/reports` cũ dùng để giữ bộ lọc khi chuyển hướng link `?tab=inventory`).
 */
export const INVENTORY_FILTERS = defineListFilters({
    q: textFilter({
        label: m.report_inventory_search_label,
        placeholder: m.report_inventory_search_placeholder,
    }),
    stock_status: selectFilter<StockStatusFilter>({
        label: m.report_inventory_stock_status,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.report_inventory_filter_all() },
            { value: 'in_stock', label: m.report_inventory_filter_in_stock() },
            { value: 'low', label: m.report_inventory_filter_low() },
            { value: 'out', label: m.report_inventory_filter_out() },
        ],
    }),
})

interface Props {
    /** Search params của route `/reports/inventory`. */
    search: Record<string, unknown>
    onSearchChange: (
        next: FilterSearchParams,
        opts?: { replace?: boolean },
    ) => void
}

/** Số cột của bảng tồn kho — hàng bung vị trí và hàng rỗng đều trải hết bảng. */
const INVENTORY_COLUMN_COUNT = 7

const statusBadge = (status: InventoryDetailItem['status']) => {
    switch (status) {
        case 'out':
            return (
                <Badge variant="destructive">
                    {m.report_inventory_status_out()}
                </Badge>
            )
        case 'low':
            return (
                <Badge
                    variant="secondary"
                    className="bg-yellow-100 text-yellow-800"
                >
                    {m.report_inventory_status_low()}
                </Badge>
            )
        default:
            return (
                <Badge variant="outline">
                    {m.report_inventory_status_ok()}
                </Badge>
            )
    }
}

export function InventoryTab({ search, onSearchChange }: Props) {
    const { values, barProps } = useListFilters({
        def: INVENTORY_FILTERS,
        search,
        onSearchChange,
        columns: 3,
    })

    const status =
        values.stock_status === 'all' ? undefined : values.stock_status
    const searchTerm = values.q.trim() || undefined

    // Bung một dòng tại một thời điểm: mỗi dòng bung là một request tồn theo vị
    // trí, mở nhiều dòng cùng lúc là bắn cả nắm request cho một thứ chỉ đọc lướt.
    const [expandedId, setExpandedId] = useState<string | null>(null)

    // Đổi bộ lọc là bảng đổi hẳn tập dòng: giữ nguyên dòng đang bung sẽ để lại
    // một request sống cho sản phẩm không còn hiển thị, và khi bộ lọc cũ quay
    // lại thì dòng đó tự mở dù người dùng không bấm.
    //
    // Reset ngay trong lúc render (pattern "adjust state on prop change" của
    // React) và đặt TRƯỚC hook tải dữ liệu: làm trong useEffect thì effect chạy
    // sau khi paint, còn đặt sau hook thì lượt render bị huỷ vẫn kịp hỏi tồn của
    // dòng vừa bị lọc mất.
    const filterKey = `${status ?? ''}|${searchTerm ?? ''}`
    const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
    let expandedProductId = expandedId
    if (prevFilterKey !== filterKey) {
        setPrevFilterKey(filterKey)
        setExpandedId(null)
        expandedProductId = null
    }

    // enabled: !!id trong hook → không có dòng nào bung thì không gọi API.
    const {
        data: positions,
        isPending: isPositionsPending,
        isError: isPositionsError,
        refetch: refetchPositions,
    } = useInventoryPositions(expandedProductId ?? '')

    // Thẻ KPI đứng ngay trên bảng nên phải đếm trên CÙNG tập dữ liệu, nếu
    // không thì "200 Hết hàng" đá với số dòng bên dưới.
    const { data: summary } = useInventorySummary({
        status,
        search: searchTerm,
    })
    // Lọc phía server; 500 dòng khớp với bản xuất Excel để bảng và file không
    // nói hai con số khác nhau trên cùng một bộ lọc.
    const { data: details = [] } = useInventoryDetails({
        status,
        search: searchTerm,
        limit: 500,
    })

    // Xuất Excel ăn theo đúng bộ lọc đang hiện — tải về một file khác hẳn thứ
    // đang nhìn thấy là bẫy.
    const handleExport = () => {
        const url = reportApi.exportExcelUrl({
            type: 'inventory',
            status,
            search: searchTerm,
        })
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const summaryItems: StatListItem[] = summary
        ? [
              {
                  label: m.report_inventory_total_skus(),
                  value: summary.total_skus.toString(),
              },
              {
                  label: m.report_inventory_stock_value(),
                  value: formatVnd(summary.total_stock_value),
              },
              {
                  label: m.report_inventory_low_stock(),
                  value: summary.low_stock_count.toString(),
                  valueClassName:
                      summary.low_stock_count > 0 ? 'text-yellow-600' : '',
              },
              {
                  label: m.report_inventory_out_of_stock(),
                  value: summary.out_of_stock_count.toString(),
                  valueClassName:
                      summary.out_of_stock_count > 0 ? 'text-red-600' : '',
              },
          ]
        : []

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const isFiltered = barProps.activeCount > 0 || !!values.q.trim()

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <Button
                    size="sm"
                    variant="outline"
                    className="h-11 w-full shrink-0 md:h-9 md:w-auto"
                    onClick={handleExport}
                >
                    {m.report_export_excel()}
                </Button>
            </div>

            <ListFilterBar {...barProps} />

            {summary && (
                <>
                    {/* Mobile: list dòng — "Giá trị tồn kho" là số tiền dài. */}
                    <StatListCard className="sm:hidden" items={summaryItems} />
                    <div className="hidden gap-4 sm:grid sm:grid-cols-4">
                        {summaryItems.map((item) => (
                            <Card key={item.label}>
                                <CardContent className="pt-4 pb-4">
                                    <div
                                        className={`text-2xl font-bold ${item.valueClassName ?? ''}`}
                                    >
                                        {item.value}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {item.label}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            )}

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.report_inventory_product()}</TableHead>
                        <TableHead>{m.report_inventory_sku()}</TableHead>
                        <TableHead className="text-right">
                            {m.report_inventory_quantity()}
                        </TableHead>
                        <TableHead className="text-right">
                            {m.report_inventory_min_level()}
                        </TableHead>
                        <TableHead className="text-right">
                            {m.report_inventory_cost_price()}
                        </TableHead>
                        <TableHead className="text-right">
                            {m.report_inventory_value()}
                        </TableHead>
                        <TableHead>{m.report_inventory_status()}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {details.map((item) => {
                        const isExpanded = expandedProductId === item.product_id
                        return (
                            <Fragment key={item.product_id}>
                                <TableRow>
                                    <TableCell className="font-medium">
                                        <button
                                            type="button"
                                            aria-expanded={isExpanded}
                                            className="flex items-center gap-1.5 text-left hover:underline"
                                            onClick={() =>
                                                setExpandedId(
                                                    isExpanded
                                                        ? null
                                                        : item.product_id,
                                                )
                                            }
                                        >
                                            <ChevronRight
                                                aria-hidden
                                                className={`size-4 shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                            />
                                            {item.product_name}
                                        </button>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {item.product_sku}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(item.quantity)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {item.min_stock_level > 0
                                            ? formatQty(item.min_stock_level)
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatVnd(item.cost_price)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatVnd(item.stock_value)}
                                    </TableCell>
                                    <TableCell>
                                        {statusBadge(item.status)}
                                    </TableCell>
                                </TableRow>
                                {isExpanded && (
                                    <InventoryPositionRows
                                        colSpan={INVENTORY_COLUMN_COUNT}
                                        items={positions}
                                        isLoading={isPositionsPending}
                                        isError={isPositionsError}
                                        onRetry={() => {
                                            void refetchPositions()
                                        }}
                                    />
                                )}
                            </Fragment>
                        )
                    })}
                    {details.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={INVENTORY_COLUMN_COUNT}
                                className="py-8"
                            >
                                <div className="flex flex-col items-center gap-3 text-center">
                                    <span className="text-muted-foreground">
                                        {isFiltered
                                            ? m.list_empty_filtered()
                                            : m.report_inventory_no_data()}
                                    </span>
                                    {isFiltered ? (
                                        <ClearFilterButton
                                            active
                                            onClear={barProps.onClear}
                                        />
                                    ) : null}
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <WarehouseMovementSection />
        </div>
    )
}
